// Marketing opt-in helper, kept in its own module so consumers (the
// MarketingOptinModal) do not transitively pull the whole general.ts god-object.

const KIOSK_ACCOUNT_CREATOR = 2;

// Returns the text colour (black or white) that reads best on `backgroundColor`,
// so a brand `primaryColor` configured per kiosk keeps a WCAG-compliant contrast.
// Falls back to white for non-hex values (named colours / CSS vars) — the
// previous hard-coded default. Uses the WCAG relative-luminance crossover (~0.179)
// between choosing black vs white text.
export function getReadableTextColor(
    backgroundColor: string
): "#000000" | "#FFFFFF" {
    const hex = backgroundColor.replace("#", "").trim();
    const full =
        hex.length === 3
            ? hex
                  .split("")
                  .map((c) => c + c)
                  .join("")
            : hex;
    if (!/^[0-9a-fA-F]{6}$/.test(full)) {
        return "#FFFFFF";
    }
    const toLinear = (value: number) => {
        const channel = value / 255;
        return channel <= 0.03928
            ? channel / 12.92
            : Math.pow((channel + 0.055) / 1.055, 2.4);
    };
    const luminance =
        0.2126 * toLinear(parseInt(full.slice(0, 2), 16)) +
        0.7152 * toLinear(parseInt(full.slice(2, 4), 16)) +
        0.0722 * toLinear(parseInt(full.slice(4, 6), 16));
    return luminance > 0.179 ? "#000000" : "#FFFFFF";
}

type GodiaAccountFormValues = {
    phoneNumber: string;
    firstName: string;
    name: string;
    email: string;
    country: string;
    postalCode: string;
    marketingConsent: boolean;
};

// Maps the account-creation form values to the CompteClientGodia request body.
// Extracted (and unit-tested) so the marketingConsent -> hasOptin mapping that
// carries the RGPD consent is covered and cannot silently regress.
export function buildGodiaAccountBody(values: GodiaAccountFormValues): {
    tel: string;
    nom: string;
    prenom: string;
    email: string;
    pays: string;
    code_postal: string;
    origin: number;
    has_optin: boolean;
} {
    return {
        tel: values.phoneNumber,
        nom: values.name,
        prenom: values.firstName,
        email: values.email,
        pays: values.country,
        code_postal: values.postalCode,
        origin: KIOSK_ACCOUNT_CREATOR,
        has_optin: Boolean(values.marketingConsent),
    };
}

/**
 * Decides whether the post-login marketing opt-in modal should be shown.
 *
 * Semantics: prompt **only while the customer has no recorded decision**
 * (`hasOptin` absent / `undefined` / `null`). Once any explicit value exists the
 * modal is skipped — note that `false` is a **valid explicit opt-out**, not an
 * anomaly. Called on every auth, so logs use `console.debug` (no hot-path noise).
 */
export function shouldPromptMarketingOptin(
    response: { hasOptin?: boolean | null } | null | undefined
): boolean {
    if (response?.hasOptin === undefined || response?.hasOptin === null) {
        console.debug(
            "marketing opt-in modal opened: customer has no recorded consent decision"
        );
        return true;
    }
    console.debug(
        response.hasOptin
            ? "marketing opt-in modal skipped: customer already opted in"
            : "marketing opt-in modal skipped: customer already opted out"
    );
    return false;
}

const CUSTOMER_OPTIN_TIMEOUT_MS = 10000;

// Only phone-legal characters, 6–20 chars total. The digit count is checked
// separately so degenerate inputs like "++++++", "((((((" or "     1" (which
// satisfy the character set but are not real numbers) are rejected.
const PHONE_NUMBER_REGEX = /^[0-9+\-\s()]{6,20}$/;
const MIN_PHONE_DIGITS = 6;

/**
 * Persists the customer's marketing decision via `POST /updateCustomer`.
 *
 * `hasOptin` is tri-state and serialized as-is in the body:
 * - `true`  → opt-in (Activer)
 * - `false` → explicit opt-out
 * - `null`  → deferred / no decision (reserved). The modal currently filters
 *   `null` upstream (Pas maintenant makes no API call), but the helper keeps the
 *   state so a future caller can clear/reset a decision if the backend supports it.
 *
 * @returns `true` when the write succeeded (HTTP 2xx), `false` otherwise.
 */
export async function updateCustomerOptin(
    middlewareApiUrl: string,
    phoneNumber: string,
    hasOptin: boolean | null
): Promise<boolean> {
    const digitCount = (phoneNumber.match(/[0-9]/g) || []).length;
    if (
        !PHONE_NUMBER_REGEX.test(phoneNumber) ||
        digitCount < MIN_PHONE_DIGITS
    ) {
        console.error("updateCustomer aborted: invalid phone number format");
        return false;
    }

    // The middleware URL comes from local kiosk config; reject malformed values
    // (e.g. a tampered config) before issuing the request.
    try {
        // eslint-disable-next-line no-new
        new URL(middlewareApiUrl);
    } catch {
        console.error("updateCustomer aborted: invalid middleware URL");
        return false;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(
        () => controller.abort(),
        CUSTOMER_OPTIN_TIMEOUT_MS
    );

    try {
        const response = await fetch(`${middlewareApiUrl}/updateCustomer`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ phoneNumber, hasOptin }),
            signal: controller.signal,
        });
        if (!response.ok) {
            console.error(`updateCustomer failed with HTTP ${response.status}`);
            return false;
        }
        return true;
    } catch (error) {
        console.error(`Error while updating customer opt-in: ${error}`);
        return false;
    } finally {
        clearTimeout(timeoutId);
    }
}
